import React, { useMemo } from "react";

/**
 * Sparkline — a tiny inline-SVG trend glyph (no axes, no tooltip): the last N
 * values as a 1.5px de-emphasis polyline, the current value as a dot in the
 * given status colour. `null` values break the line (gap), nothing is
 * interpolated. The y scale is fixed to [0, max] so sparklines of different
 * peers are comparable at a glance; values above `max` are clamped.
 */
const Sparkline = ({
  values,
  width = 48,
  height = 14,
  max = 100,
  warnAt = null,
  lastColor = "currentColor",
  stroke = "rgba(255,255,255,0.65)",
  className = "",
  title,
}) => {
  const { segments, last, warnY } = useMemo(() => {
    const vals = Array.isArray(values) ? values : [];
    const n = Math.max(vals.length, 2);
    const stepX = (width - 4) / (n - 1);
    const scaleY = (v) => {
      const clamped = Math.max(0, Math.min(max, v));
      return height - 2 - (clamped / max) * (height - 4);
    };
    const segs = [];
    let current = [];
    let lastPoint = null;
    vals.forEach((v, i) => {
      if (v == null || !Number.isFinite(v)) {
        if (current.length) segs.push(current);
        current = [];
        return;
      }
      const x = 2 + i * stepX;
      const y = scaleY(v);
      current.push(`${x.toFixed(1)},${y.toFixed(1)}`);
      lastPoint = { x, y };
    });
    if (current.length) segs.push(current);
    return {
      segments: segs,
      last: lastPoint,
      warnY: warnAt != null ? scaleY(warnAt) : null,
    };
  }, [values, width, height, max, warnAt]);

  return (
    <svg
      className={`peer-sparkline ${className}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
    >
      {title && <title>{title}</title>}
      {warnY != null && (
        <line x1="0" x2={width} y1={warnY} y2={warnY} stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      )}
      {segments.map((pts, i) => (
        <polyline
          key={i}
          points={pts.join(" ")}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {last && <circle cx={last.x} cy={last.y} r="2" fill={lastColor} stroke="#222" strokeWidth="1" />}
    </svg>
  );
};

export default React.memo(Sparkline);
